import React, { useState, useEffect } from "react";
import { getFirestore, collection, getDocs, addDoc, query, where, orderBy, limit, serverTimestamp } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import axios from "axios";
import { useNotification } from "@mmfestival/shared-components";
import { LuSend, LuCheck, LuX, LuLoader, LuEye, LuPlay, LuPause, LuTerminal, LuMail } from "react-icons/lu";

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  (window.location.hostname === "localhost"
    ? "http://localhost:3000"
    : "");

if (!API_BASE) {
  throw new Error("VITE_API_BASE_URL is missing");
}

// Get auth headers helper
const getAuthHeaders = async () => {
    const auth = getAuth();
    if (!auth.currentUser) throw new Error("Not authenticated");
    const token = await auth.currentUser.getIdToken(true);
    return { Authorization: `Bearer ${token}` };
};
export const BroadcastEmail = () => {
    const { showNotification } = useNotification();
    const db = getFirestore();

    const [activeTab, setActiveTab] = useState("compose");
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState("");
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState("");
    
    // Template type (bukan filter status!)
    const [templateType, setTemplateType] = useState("success");
    
    const [audience, setAudience] = useState([]);
    const [loadingAudience, setLoadingAudience] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedEmails, setSelectedEmails] = useState(new Set());
    const [sending, setSending] = useState(false);
    const [logs, setLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(true);
    
    // Testing State
    const [testEmail, setTestEmail] = useState("");

    // Custom Email States
    const [customSubject, setCustomSubject] = useState("");
    const [customContent, setCustomContent] = useState("");
    const [manualEmails, setManualEmails] = useState("");

    // BATCH SENDING STATE (ROBUST PROGRESS)
    const [batchState, setBatchState] = useState({
        active: false,
        paused: false,
        completed: false,
        total: 0,
        processed: 0,
        successCount: 0,
        failCount: 0,
        recipients: [],
        currentIndex: 0,
        logs: [],
        batchSize: 5
    });

    // Preview State
    const [showPreview, setShowPreview] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewTitle, setPreviewTitle] = useState("Email Preview");
    const [loadingLogPreview, setLoadingLogPreview] = useState(null); // log.id being loaded

    const filteredAudience = audience.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
        p.email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    useEffect(() => { fetchEvents(); fetchLogs(); }, []);

    const fetchEvents = async () => {
        try {
            const snapshot = await getDocs(collection(db, "semuaKegiatan"));
            const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setEvents(list);
            if (list.length > 0) setSelectedEvent(list[0].id);
        } catch (err) { console.error(err); }
    };

    const fetchLogs = async () => {
        setLoadingLogs(true);
        try {
            const q = query(collection(db, "emailLogs"), orderBy("createdAt", "desc"), limit(50));
            const snapshot = await getDocs(q);
            setLogs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
            console.error(err);
            showNotification("error", "Gagal membaca emailLogs: " + (err?.message || String(err)));
        }
        finally { setLoadingLogs(false); }
    };

    useEffect(() => {
        if (!selectedEvent) return;
        const evt = events.find(e => e.id === selectedEvent);
        setCategories(evt?.categories ? [...new Set(evt.categories.map(c => c.categoryName))] : []);
        setSelectedCategory("");
    }, [selectedEvent, events]);

    // Selalu fetch peserta SETTLEMENT saja
    useEffect(() => { if (selectedEvent) fetchAudience(); }, [selectedEvent, selectedCategory]);

    const fetchAudience = async () => {
        if (!selectedEvent) return;
        setLoadingAudience(true);
        setAudience([]);
        setSelectedEmails(new Set());
        try {
            // SELALU fetch SETTLEMENT peserta saja
            const constraints = [where("transaction_status", "==", "settlement")];
            if (selectedCategory) constraints.push(where("kategori", "==", selectedCategory));
            const q = query(collection(db, `pendaftaran${selectedEvent}`), ...constraints);
            const snapshot = await getDocs(q);
            const list = [];
            snapshot.forEach(doc => {
                const d = doc.data();
                if (d.email) list.push({ 
                    email: d.email, 
                    name: d.namaLengkap || "Runner", 
                    kategori: d.kategori || "-",
                    orderId: d.orderId || d.order_id || doc.id,
                    // CRITICAL FIX: Prioritize USD amount for PayPal, else use gross_amount, else harga
                    grossAmount: d.payment_type === 'paypal' ? (d.paypalCaptureAmount || d.gross_amount || 0) : (d.gross_amount || d.harga || 0),
                    expiryTime: d.expiry_time || null,
                    paymentType: d.payment_type || "-",
                    vaBank: d.va_bank_first || (d.va_numbers?.[0]?.bank) || "-",
                    vaNumber: d.va_number_first || (d.va_numbers?.[0]?.va_number) || "-",
                    billerCode: d.biller_code || "-",
                    billKey: d.bill_key || "-",
                    phone: d.telepon || d.customer_details?.phone || "-",
                    transactionTime: d.transaction_time || null
                });
            });
            setAudience(list);
        } catch (err) { console.error(err); }
        finally { setLoadingAudience(false); }
    };

    const toggleSelect = (email) => {
        const s = new Set(selectedEmails);
        s.has(email) ? s.delete(email) : s.add(email);
        setSelectedEmails(s);
    };

    const toggleAll = () => {
        setSelectedEmails(selectedEmails.size === filteredAudience.length ? new Set() : new Set(filteredAudience.map(p => p.email)));
    };

    // === BATCH PROCESSING LOGIC ===
    useEffect(() => {
        if (!batchState.active || batchState.paused || batchState.completed) return;

        const processBatch = async () => {
            const { recipients, currentIndex, batchSize, successCount, failCount } = batchState;
            
            // Check if done
            if (currentIndex >= recipients.length) {
                setBatchState(prev => ({ ...prev, completed: true, active: false }));
                showNotification("success", "Broadcast Completed!");
                
                // Final Log Save
                await addDoc(collection(db, "emailLogs"), {
                    event: selectedEvent, 
                    templateType: templateType,
                    category: selectedCategory || "Semua",
                    recipientCount: recipients.length, 
                    recipientEmails: recipients.map(t => t.email), 
                    orderIds: recipients.map(t => t.orderId).filter(Boolean),
                    sent: successCount, 
                    failed: failCount, 
                    success: failCount === 0,
                    createdAt: serverTimestamp(),
                    completedAt: serverTimestamp(),
                    mode: "batch_robust"
                });
                fetchLogs();
                return;
            }

            // Get Batch
            const batch = recipients.slice(currentIndex, currentIndex + batchSize);
            
            try {
                const headers = await getAuthHeaders();
                const eventData = events.find(e => e.id === selectedEvent);
                const eventTitle = eventData?.title || selectedEvent;
                
                const emailSubject = templateType === 'custom' 
                    ? customSubject 
                    : templateType === 'race_reminder_h1' ? 'Race Day Reminder (H-1) | Bali Mountain Marathon'
                    : `${eventTitle} - ${templateType === 'success' ? 'Registration Confirmed' : templateType === 'pending' ? 'Payment Reminder' : templateType === 'race_info' ? 'Race Week Information' : templateType === 'registration_reminder' ? 'Race Weekend Info' : 'Registration Expired'}`;

                // Send Batch API Call
                const res = await axios.post(`${API_BASE}/api/send-brevo-email`, {
                    recipients: batch,
                    subject: emailSubject,
                    templateId: null, 
                    type: templateType,
                    eventId: selectedEvent,
                    eventName: eventTitle,
                    customHtmlContent: templateType === 'custom' ? customContent : null
                }, { headers });

                // Update State
                const newSuccess = res.data.sent || 0;
                const newFailed = res.data.failed || 0;
                
                setBatchState(prev => ({
                    ...prev,
                    processed: prev.processed + batch.length,
                    successCount: prev.successCount + newSuccess,
                    failCount: prev.failCount + newFailed,
                    currentIndex: prev.currentIndex + batch.length,
                    logs: [...prev.logs, ...batch.map((r, i) => ({ 
                        email: r.email, 
                        status: res.data.errors?.find(e => e.email === r.email) ? 'error' : 'success',
                        msg: res.data.errors?.find(e => e.email === r.email)?.error || 'Sent'
                    }))].slice(-20) // Keep last 20 logs for display
                }));

            } catch (err) {
                // Batch Failed (Network Error, etc)
                console.error("Batch Error:", err);
                const errorMsg = err.response?.data?.error || err.message;
                
                setBatchState(prev => ({
                    ...prev,
                    processed: prev.processed + batch.length,
                    failCount: prev.failCount + batch.length,
                    currentIndex: prev.currentIndex + batch.length,
                    logs: [...prev.logs, ...batch.map(r => ({ email: r.email, status: 'error', msg: errorMsg }))].slice(-20)
                }));
            }
        };

        // Delay 1s between batches to be safe and allow UI update
        const timer = setTimeout(processBatch, 1000);
        return () => clearTimeout(timer);
    }, [batchState]);

    const handleSend = async () => {
        // Parse manual emails
        const manualList = manualEmails
            .split(/[,\n]/)
            .map(e => e.trim())
            .filter(e => e && e.includes('@'));
        
        const participantTargets = filteredAudience.filter(p => selectedEmails.has(p.email));
        const totalRecipients = participantTargets.length + manualList.length;
        
        if (totalRecipients === 0) return showNotification("error", "Pilih minimal 1 penerima atau masukkan email manual.");
        
        if (templateType === 'custom' && (!customSubject || !customContent)) {
            return showNotification("error", "Masukkan subject dan konten email untuk template Custom.");
        }
        
        if (!window.confirm(`Kirim email template "${templateType.toUpperCase()}" ke ${totalRecipients} penerima?`)) return;

        // Build recipients list
        const allRecipients = [
            ...participantTargets.map(p => ({ 
                name: p.name, 
                email: p.email, 
                kategori: p.kategori,
                orderId: p.orderId,
                grossAmount: p.grossAmount,
                expiryTime: p.expiryTime,
                paymentType: p.paymentType,
                vaBank: p.vaBank,
                vaNumber: p.vaNumber,
                billerCode: p.billerCode,
                billKey: p.billKey,
                phone: p.phone,
                transactionTime: p.transactionTime
            })),
            ...manualList.map(email => ({
                name: email.split('@')[0],
                email: email,
                kategori: '-',
                orderId: '-',
                grossAmount: 0,
                expiryTime: null,
                paymentType: '-',
                vaBank: '-',
                vaNumber: '-',
                billerCode: '-',
                billKey: '-',
                phone: '-',
                transactionTime: null
            }))
        ];

        // Initialize Batch State
        setBatchState({
            active: true,
            paused: false,
            completed: false,
            total: allRecipients.length,
            processed: 0,
            successCount: 0,
            failCount: 0,
            recipients: allRecipients,
            currentIndex: 0,
            logs: [],
            batchSize: 5
        });
    };

    const handleLogPreview = async (log) => {
        setLoadingLogPreview(log.id);
        try {
            const headers = await getAuthHeaders();
            const eventTitle = log.event || "Event";
            const firstEmail = log.recipientEmails?.[0];
            const firstOrderId = log.orderId || (log.orderIds && log.orderIds[0]);

            // Try to resolve correct collection ID (prefer log.eventId if exists)
            let collectionId = log.eventId || log.event; 
            
            // Resolve full event title from semuaKegiatan for preview header
            let fullEventTitle = log.eventTitle || log.event || "Event";
            if (collectionId) {
                try {
                    const eventDoc = await getDoc(doc(db, "semuaKegiatan", collectionId));
                    if (eventDoc.exists()) {
                        fullEventTitle = eventDoc.data().title || fullEventTitle;
                    }
                } catch (e) {
                    console.warn("Could not fetch full event title:", e);
                }
            }

            // Fallback for old logs: if collectionId is likely a title (has space etc), try to find ID
            if (collectionId && (collectionId.includes(" ") || !events.find(e => e.id === collectionId))) {
                const found = events.find(e => e.title === log.event);
                if (found) collectionId = found.id;
            }

            // Try to fetch actual participant data from Firestore
            let participant = null;
            if (collectionId) {
                try {
                    // Method A: Lookup by Order ID (Most accurate)
                    if (firstOrderId && firstOrderId !== "-") {
                        // Check both orderId and order_id (PayPal/Midtrans inconsistency)
                        let snap = await getDocs(query(collection(db, `pendaftaran${collectionId}`), where("orderId", "==", firstOrderId), limit(1)));
                        if (snap.empty) {
                            snap = await getDocs(query(collection(db, `pendaftaran${collectionId}`), where("order_id", "==", firstOrderId), limit(1)));
                        }

                        if (!snap.empty) {
                            const d = snap.docs[0].data();
                            // Normalize data into uniform format
                            participant = {
                                id: snap.docs[0].id,
                                name: d.namaLengkap || d.email || firstEmail,
                                kategori: d.kategori || log.category || "-",
                                orderId: d.orderId || d.order_id || snap.docs[0].id,
                                // CRITICAL FIX: Prioritize USD amount for PayPal, else use gross_amount, else harga
                                grossAmount: d.payment_type === 'paypal' ? (d.paypalCaptureAmount || d.gross_amount || 0) : (d.gross_amount || d.harga || 0),
                                expiryTime: d.expiry_time || null,
                                paymentType: d.payment_type || "-",
                                vaBank: d.va_bank_first || d.va_numbers?.[0]?.bank || "-",
                                vaNumber: d.va_number_first || d.va_numbers?.[0]?.va_number || "-",
                                billerCode: d.biller_code || "-",
                                billKey: d.bill_key || "-",
                                phone: d.telepon || "-",
                                transactionTime: d.transaction_time || null,
                            };
                        }
                    } 
                    
                    // Method B: Fallback to email lookup ONLY if Order ID was not provided in logs
                    if (!participant && firstEmail && (!firstOrderId || firstOrderId === "-")) {
                        const q = query(
                            collection(db, `pendaftaran${collectionId}`),
                            where("email", "==", firstEmail),
                            limit(1)
                        );
                        const snap = await getDocs(q);
                        if (!snap.empty) {
                            const d = snap.docs[0].data();
                            participant = {
                                name: d.namaLengkap || firstEmail,
                                kategori: d.kategori || log.category || "-",
                                orderId: d.orderId || d.order_id || snap.docs[0].id,
                                grossAmount: d.gross_amount || d.harga || 0,
                                expiryTime: d.expiry_time || null,
                                paymentType: d.payment_type || "-",
                                vaBank: d.va_bank_first || d.va_numbers?.[0]?.bank || "-",
                                vaNumber: d.va_number_first || d.va_numbers?.[0]?.va_number || "-",
                                billerCode: d.biller_code || "-",
                                billKey: d.bill_key || "-",
                                phone: d.telepon || "-",
                                transactionTime: d.transaction_time || null,
                            };
                        }
                    }
                } catch (lookupErr) {
                    console.warn("Could not look up participant:", lookupErr);
                }
            }

            const res = await axios.post(`${API_BASE}/api/preview-email`, {
                recipientName: participant?.name || firstEmail || "Preview User",
                kategori: participant?.kategori || log.category || "-",
                type: log.templateType || "success",
                eventName: eventTitle,
                orderId: participant?.orderId || firstOrderId || "-",
                grossAmount: participant?.grossAmount || 0,
                expiryTime: participant?.expiryTime || null,
                paymentType: participant?.paymentType || "-",
                vaBank: participant?.vaBank || "-",
                vaNumber: participant?.vaNumber || "-",
                billerCode: participant?.billerCode || "-",
                billKey: participant?.billKey || "-",
                phone: participant?.phone || "-",
                transactionTime: participant?.transactionTime || new Date().toISOString(),
                customHtmlContent: null
            }, { headers });

            setPreviewHtml(res.data.html);
            setPreviewTitle(`Preview: ${log.templateType?.toUpperCase() || "EMAIL"} — ${fullEventTitle}`);
            setShowPreview(true);
        } catch (err) {
            showNotification("error", "Gagal load preview: " + err.message);
        } finally {
            setLoadingLogPreview(null);
        }
    };

    const handlePreview = async () => {
        try {
            const headers = await getAuthHeaders();
            const eventData = events.find(e => e.id === selectedEvent);
            const eventTitle = eventData?.title || selectedEvent;
            
            const emailSubject = templateType === 'custom' 
                ? customSubject 
                : templateType === 'race_reminder_h1' ? 'Race Day Reminder (H-1) | Bali Mountain Marathon'
                : `${eventTitle} - ${templateType === 'success' ? 'Registration Confirmed' : templateType === 'pending' ? 'Payment Reminder' : templateType === 'race_info' ? 'Race Week Information' : templateType === 'registration_reminder' ? 'Race Weekend Info' : 'Registration Expired'}`;
            
            const res = await axios.post(`${API_BASE}/api/preview-email`, {
                recipientName: "Preview User",
                kategori: "Preview Category",
                type: templateType,
                eventName: eventTitle,
                orderId: "PREVIEW-123",
                grossAmount: 150000,
                expiryTime: null,
                paymentType: "-",
                vaBank: "-",
                vaNumber: "-",
                billerCode: "-",
                billKey: "-",
                phone: "-",
                transactionTime: new Date().toISOString(),
                customHtmlContent: templateType === 'custom' ? customContent : null
            }, { headers });
            
            setPreviewHtml(res.data.html);
            setPreviewTitle(`Preview: ${templateType.toUpperCase()} — ${eventTitle}`);
            setShowPreview(true);
        } catch (err) {
            showNotification("error", "Gagal load preview: " + err.message);
        } finally {
            setLoadingPreview(false);
        }
    };

    const handleTestPayPal = async () => {
        if (!testEmail) return showNotification("error", "Masukkan email tujuan test.");
        setSending(true);
        try {
            const headers = await getAuthHeaders();
            await axios.post(`${API_BASE}/api/send-brevo-email`, {
                recipients: [{ 
                    name: "Test User", 
                    email: testEmail, 
                    kategori: "Test Category",
                    orderId: "TEST-PAYPAL-" + Date.now(),
                    grossAmount: 15,
                    expiryTime: null,
                    paymentType: "paypal",
                    vaBank: "-",
                    vaNumber: "-",
                    billerCode: "-",
                    billKey: "-",
                    phone: "08123456789",
                    transactionTime: new Date().toISOString()
                }],
                subject: `[TEST] PayPal Success Email`,
                templateId: null, 
                type: 'success',
                eventName: "MM Festival 2025"
            }, { headers });
            showNotification("success", `Test email sent to ${testEmail}`);
        } catch (err) {
            showNotification("error", err.message);
        } finally {
            setSending(false);
        }
    };

    const formatDate = (ts) => {
        if (!ts) return "-";
        const d = ts.toDate ? ts.toDate() : new Date(ts);
        return d.toLocaleString('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <div className="space-y-4 pb-10">
            {/* Header with Dashboard Style */}
            <div className="bg-white rounded-xl p-4 border border-slate-200 text-slate-800 relative overflow-hidden">
               <div className="absolute top-0 right-0 p-4 opacity-5 text-slate-200 pointer-events-none">
                  <LuMail size={120} />
               </div>
               <div className="relative z-10">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                     <div>
                        <p className="text-slate-500 font-medium mb-1 flex items-center gap-2 text-xs uppercase tracking-wider">
                           <LuMail size={14} /> Communication
                        </p>
                        <h1 className="text-2xl md:text-3xl font-bold mb-2 break-words"> Email Center</h1>
                        <p className="text-slate-500 text-sm max-w-xl">
                            Send broadcast emails to confirmed participants.
                        </p>
                     </div>
                  </div>
               </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 mb-4 border-b">
                <button onClick={() => setActiveTab("compose")} className={`px-4 py-2 text-sm font-medium border rounded-t-lg ${activeTab === "compose" ? "bg-white border-gray-300 border-b-white -mb-px" : "bg-gray-100 border-transparent text-gray-500"}`}>
                    Compose Broadcast
                </button>
                <button onClick={() => setActiveTab("logs")} className={`px-4 py-2 text-sm font-medium border rounded-t-lg ${activeTab === "logs" ? "bg-white border-gray-300 border-b-white -mb-px" : "bg-gray-100 border-transparent text-gray-500"}`}>
                    Email Logs
                </button>
                <button onClick={() => setActiveTab("testing")} className={`px-4 py-2 text-sm font-medium border rounded-t-lg ${activeTab === "testing" ? "bg-white border-gray-300 border-b-white -mb-px" : "bg-gray-100 border-transparent text-gray-500"}`}>
                    Testing
                </button>
            </div>

            {activeTab === "compose" ? (
                <div className="flex gap-6">
                    {/* Left: Participant Selection */}
                    <div className="w-72 flex-shrink-0">
                        <h3 className="font-semibold text-gray-800 mb-2 text-sm">Pilih Peserta</h3>
                        
                        {/* Filters */}
                        <div className="space-y-2 mb-3">
                            <div>
                                <label className="text-xs text-gray-500">Event</label>
                                <select className="w-full p-2 text-sm border rounded" value={selectedEvent} onChange={e => setSelectedEvent(e.target.value)}>
                                    {events.map(e => <option key={e.id} value={e.id}>{e.title || e.id}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500">Kategori</label>
                                <select className="w-full p-2 text-sm border rounded" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
                                    <option value="">Semua Kategori</option>
                                    {categories.map((c, i) => <option key={i} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-gray-500">Email Template</label>
                                <select className="w-full p-2 text-sm border rounded bg-white" value={templateType} onChange={e => setTemplateType(e.target.value)}>
                                    <option value="success">Registration Confirmed</option>
                                    <option value="pending">Payment Pending</option>
                                    <option value="expired">Registration Expired</option>
                                    <option value="race_info">📋 Race Week Info (BMM 2026)</option>
                                    <option value="race_reminder_h1">🔔 Race Day Reminder (H-1)</option>
                                    <option value="registration_reminder">👋 Registration Reminder (Greeting)</option>
                                    <option value="custom">Custom / Announcement</option>
                                </select>
                            </div>
                        </div>

                        <label className="flex items-center gap-2 text-xs text-gray-600 mb-2">
                            <input type="checkbox" onChange={toggleAll} checked={selectedEmails.size > 0 && selectedEmails.size === filteredAudience.length} className="accent-gray-800"/>
                            Select All ({filteredAudience.length})
                        </label>

                        <input type="text" placeholder="🔍 Search..." className="w-full p-2 text-sm border rounded mb-2" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}/>

                        {/* Info: Hanya settlement */}
                        <div className="text-xs text-gray-400 mb-2 bg-gray-50 p-2 rounded">
                            📋 Menampilkan peserta <strong>SETTLEMENT</strong> saja
                        </div>

                        {/* Participant List */}
                        <div className="border rounded h-64 overflow-y-auto">
                            {loadingAudience ? (
                                <div className="p-4 text-center text-gray-400 text-sm"><LuLoader className="inline animate-spin"/> Loading...</div>
                            ) : filteredAudience.length === 0 ? (
                                <div className="p-4 text-center text-gray-400 text-sm">No participants</div>
                            ) : (
                                filteredAudience.map((p, i) => (
                                    <div key={i} onClick={() => toggleSelect(p.email)} className={`p-2 border-b cursor-pointer text-sm ${selectedEmails.has(p.email) ? 'bg-slate-50' : 'hover:bg-gray-50'}`}>
                                        <div className="font-medium text-gray-900">{p.name}</div>
                                        <div className="text-gray-500 text-xs">{p.email}</div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Manual Email Input */}
                        <div className="mt-4 bg-yellow-50 p-3 rounded border border-yellow-200">
                            <label className="text-sm font-bold text-gray-800 block mb-1">Tambah Penerima Manual (Non-Peserta)</label>
                            <textarea 
                                className="w-full p-2 text-sm border rounded h-24 resize-none focus:ring-2 focus:ring-slate-400"
                                placeholder="email1@example.com, email2@example.com (Dapat diinput banyak sekaligus)"
                                value={manualEmails}
                                onChange={e => setManualEmails(e.target.value)}
                            />
                            <p className="text-xs text-gray-600 mt-1">
                                <strong>Fitur Custom Penerima:</strong> Masukkan daftar email di sini untuk mengirim ke orang yang bukan peserta (misal: tamu, sponsor, atau tes). Pisahkan dengan koma atau enter.
                            </p>
                        </div>
                    </div>

                    {/* Right: Email Form */}
                    <div className="flex-1">
                        <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                                <label className="text-xs font-medium text-gray-600">To Name</label>
                                <input readOnly value={selectedEmails.size > 0 ? `${selectedEmails.size} selected` : "-"} className="w-full p-2 text-sm border rounded bg-gray-50"/>
                            </div>
                            <div>
                                <label className="text-xs font-medium text-gray-600">To Email</label>
                                <input readOnly value={selectedEmails.size === 1 ? [...selectedEmails][0] : selectedEmails.size > 1 ? "Multiple" : "-"} className="w-full p-2 text-sm border rounded bg-gray-50"/>
                            </div>
                        </div>
                        
                        <div className="mb-3">
                            <label className="text-xs font-medium text-gray-600">Subject</label>
                            {templateType === 'custom' ? (
                                <input 
                                    type="text"
                                    value={customSubject}
                                    onChange={e => setCustomSubject(e.target.value)}
                                    placeholder="Masukkan subject email..."
                                    className="w-full p-2 text-sm border rounded"
                                />
                            ) : (
                                <input readOnly value={
                                    templateType === 'race_reminder_h1' ? 'Race Day Reminder (H-1) | Bali Mountain Marathon'
                                    : `${selectedEvent} - ${templateType === 'success' ? 'Konfirmasi Pendaftaran' : templateType === 'pending' ? 'Reminder Pembayaran' : templateType === 'race_info' ? 'Race Week Information' : templateType === 'registration_reminder' ? 'Race Weekend Info' : 'Registration Expired'}`
                                } className="w-full p-2 text-sm border rounded bg-gray-50"/>
                            )}
                        </div>

                        <div className="mb-3">
                            <label className="text-xs font-medium text-gray-600">{templateType === 'custom' ? 'Email Content (HTML)' : 'Template Preview'}</label>
                            {templateType === 'custom' ? (
                                <textarea 
                                    className="w-full p-3 text-sm border rounded h-64 resize-none font-mono"
                                    placeholder="Paste konten email di sini (text atau HTML)..."
                                    value={customContent}
                                    onChange={e => setCustomContent(e.target.value)}
                                />
                            ) : (
                                <div className="border rounded p-4 bg-gray-50 text-sm">
                                    {templateType === 'success' ? (
                                        <div>
                                            <p className="text-gray-800 font-semibold mb-2">Registration Confirmed</p>
                                            <p className="text-gray-600">Dear <strong>[Recipient Name]</strong>,</p>
                                            <p className="text-gray-500 text-xs mt-2">Thank you for registering. Your payment has been received and confirmed...</p>
                                        </div>
                                    ) : templateType === 'pending' ? (
                                        <div>
                                            <p className="text-amber-600 font-semibold mb-2">Payment Pending</p>
                                            <p className="text-gray-600">Dear <strong>[Recipient Name]</strong>,</p>
                                            <p className="text-gray-500 text-xs mt-2">Your registration is awaiting payment. Please complete your payment...</p>
                                        </div>
                                    ) : templateType === 'race_info' ? (
                                        <div>
                                            <p className="text-slate-800 font-semibold mb-2">📋 Race Week Information</p>
                                            <p className="text-gray-600">Dear <strong>[Recipient Name]</strong>,</p>
                                            <p className="text-gray-500 text-xs mt-2">Complete race week info: Race Kit Collection, Briefing, Course, Safety, Equipment, Drop Bag, Parking, Finish Line, Podium...</p>
                                            <p className="text-slate-600 text-xs mt-2 font-medium">✓ Template lengkap sudah tersedia di sistem</p>
                                        </div>
                                    ) : templateType === 'registration_reminder' ? (
                                        <div>
                                            <p className="text-slate-800 font-semibold mb-2">👋 You're Registered</p>
                                            <p className="text-gray-600">Dear <strong>[Recipient Name]</strong>,</p>
                                            <p className="text-gray-500 text-xs mt-2">Friendly reminder — you are officially registered for [Event]. Shows Order ID, Event, Category, Status. No payment/amount info.</p>
                                            <p className="text-slate-600 text-xs mt-2 font-medium">✓ Professional greeting template</p>
                                        </div>
                                    ) : templateType === 'race_reminder_h1' ? (
                                        <div>
                                            <p className="text-slate-900 font-semibold mb-2">🔔 Race Day Reminder (H-1)</p>
                                            <p className="text-gray-600">Dear <strong>[Recipient Name]</strong>,</p>
                                            <p className="text-gray-500 text-xs mt-2">Final reminders: Race Day, Village Loc, Briefing, Gear, Navigation...</p>
                                            <p className="text-slate-600 text-xs mt-2 font-medium">✓ Template lengkap sudah tersedia di sistem</p>
                                        </div>
                                    ) : (
                                        <div>
                                            <p className="text-red-600 font-semibold mb-2">Registration Expired</p>
                                            <p className="text-gray-600">Dear <strong>[Recipient Name]</strong>,</p>
                                            <p className="text-gray-500 text-xs mt-2">Your payment deadline has passed and your registration has expired...</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <p className="text-xs text-gray-400 mb-4">*Email will be personalized with recipient name and event details.</p>

                        {/* ACTION BUTTONS */}
                        <div className="flex gap-3">
                            <button 
                                onClick={handlePreview}
                                disabled={loadingPreview}
                                className="flex-1 bg-white border-2 border-gray-300 hover:border-gray-400 text-gray-700 py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-40"
                            >
                                {loadingPreview ? <LuLoader className="animate-spin"/> : <LuEye />}
                                Preview
                            </button>
                            <button 
                                onClick={handleSend}
                                disabled={sending || (selectedEmails.size === 0 && !manualEmails.trim())}
                                className="flex-1 bg-gray-900 hover:bg-black text-white py-3 px-4 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {sending ? <LuLoader className="animate-spin"/> : <LuSend />}
                                Send {(selectedEmails.size > 0 || manualEmails.trim()) && `(${selectedEmails.size}${manualEmails.trim() ? '+' : ''})`}
                            </button>
                        </div>
                    </div>
                </div>
            ) : activeTab === "logs" ? (
                /* Email Logs Tab */
                <div className="border rounded overflow-hidden">
                    <div className="max-h-96 overflow-y-auto">
                        {loadingLogs ? (
                            <div className="p-6 text-center text-gray-400"><LuLoader className="inline animate-spin"/> Loading...</div>
                        ) : logs.length === 0 ? (
                            <div className="p-6 text-center text-gray-400">No logs</div>
                        ) : (
                            <table className="w-full text-sm">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr className="text-left text-gray-600 text-xs uppercase">
                                        <th className="p-3">Date</th>
                                        <th className="p-3">Recipient</th>
                                        <th className="p-3">Template</th>
                                        <th className="p-3">Status</th>
                                        <th className="p-3">Info</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {logs.map(log => (
                                        <tr 
                                            key={log.id} 
                                            className="border-t hover:bg-blue-50 cursor-pointer transition-colors"
                                            onClick={() => handleLogPreview(log)}
                                            title="Klik untuk preview isi email"
                                        >
                                            <td className="p-3 text-gray-600">{formatDate(log.createdAt)}</td>
                                            <td className="p-3">
                                                <div className="font-medium">{log.recipientCount} recipients</div>
                                                <div className="text-gray-400 text-xs truncate max-w-[150px]">{log.recipientEmails?.[0]}</div>
                                            </td>
                                            <td className="p-3">{log.templateType?.toUpperCase() || log.status?.toUpperCase()}</td>
                                            <td className="p-3">
                                                <span className={`px-2 py-1 rounded text-xs ${log.success ? 'bg-slate-100 text-slate-700' : 'bg-red-100 text-red-700'}`}>
                                                    {log.success ? 'DELIVERED' : 'FAILED'}
                                                </span>
                                            </td>
                                            <td className="p-3 text-xs text-gray-500">
                                                <div>{log.sent} sent, {log.failed} failed</div>
                                                <div className="text-blue-500 text-[10px] mt-0.5 flex items-center gap-1">
                                                    {loadingLogPreview === log.id 
                                                        ? <><LuLoader className="inline animate-spin" size={10}/> Loading...</>
                                                        : <><LuEye size={10}/> Klik untuk preview</>
                                                    }
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            ) : null}

            {activeTab === "testing" && (
                <div className="border rounded p-6 bg-white max-w-lg">
                    <h3 className="text-lg font-bold mb-4">Test PayPal Email</h3>
                    <p className="text-sm text-gray-600 mb-4">
                        Kirim simulasi email sukses bayar PayPal tanpa transaksi real.
                    </p>
                    
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email</label>
                        <input 
                            type="email" 
                            className="w-full p-2 border rounded" 
                            placeholder="Enter email..." 
                            value={testEmail}
                            onChange={e => setTestEmail(e.target.value)}
                        />
                    </div>

                    <button 
                        onClick={handleTestPayPal}
                        disabled={sending}
                        className="w-full bg-slate-800 hover:bg-slate-900 text-white py-2 px-4 rounded font-medium flex items-center justify-center gap-2"
                    >
                        {sending ? <LuLoader className="animate-spin"/> : <LuSend />}
                        Test Send
                    </button>
                </div>
            )}

            {/* Progress Modal (Batch Sending) */}
            {(batchState.active || batchState.completed) && (
                <div className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4" data-aos="fade-up">
                    <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full overflow-hidden">
                        {/* Header */}
                        <div className={`p-4 border-b flex justify-between items-center ${batchState.completed ? 'bg-slate-50' : 'bg-slate-50'}`}>
                            <h3 className="font-bold text-lg flex items-center gap-2">
                                {batchState.completed 
                                    ? <span className="text-slate-800 flex items-center gap-2"><LuCheck/> Broadcast Selesai</span>
                                    : <span className="text-slate-700 flex items-center gap-2"><LuLoader className="animate-spin"/> Mengirim Broadcast...</span>
                                }
                            </h3>
                            {!batchState.completed && (
                                <button onClick={() => setBatchState(prev => ({...prev, paused: !prev.paused}))} className="text-sm font-semibold px-3 py-1 bg-white border rounded shadow-sm hover:bg-gray-50">
                                    {batchState.paused ? <span className="flex items-center gap-1"><LuPlay/> Resume</span> : <span className="flex items-center gap-1"><LuPause/> Pause</span>}
                                </button>
                            )}
                        </div>
                        
                        {/* Body */}
                        <div className="p-6">
                            {/* Summary Stats */}
                            <div className="grid grid-cols-3 gap-4 mb-6 text-center">
                                <div className="p-3 bg-gray-50 rounded">
                                    <div className="text-2xl font-bold text-gray-800">{batchState.total}</div>
                                    <div className="text-xs text-gray-500 uppercase font-semibold">Total</div>
                                </div>
                                <div className="p-3 bg-slate-50 rounded border border-slate-200">
                                    <div className="text-2xl font-bold text-slate-800">{batchState.successCount}</div>
                                    <div className="text-xs text-gray-500 uppercase font-semibold">Berhasil</div>
                                </div>
                                <div className="p-3 bg-red-50 rounded border border-red-100">
                                    <div className="text-2xl font-bold text-red-600">{batchState.failCount}</div>
                                    <div className="text-xs text-gray-500 uppercase font-semibold">Gagal</div>
                                </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="mb-2 flex justify-between text-sm">
                                <span className="font-medium text-gray-700">Progress: {Math.round((batchState.processed / batchState.total) * 100)}%</span>
                                <span className="text-gray-500">{batchState.processed} / {batchState.total}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-4 mb-6 overflow-hidden">
                                <div 
                                    className={`h-4 transition-all duration-500 ${batchState.paused ? 'bg-amber-400' : batchState.completed ? 'bg-slate-700' : 'bg-slate-800'}`} 
                                    style={{ width: `${(batchState.processed / batchState.total) * 100}%` }}
                                ></div>
                            </div>

                            {/* Live Logs */}
                            <div className="bg-gray-900 rounded-lg p-3 h-48 overflow-y-auto font-mono text-xs text-gray-300">
                                <div className="sticky top-0 bg-gray-900 pb-2 border-b border-gray-700 mb-2 flex items-center gap-2 text-white font-semibold">
                                    <LuTerminal/> Live Activity Log
                                </div>
                                {batchState.logs.length === 0 && <span className="text-gray-600 italic">Waiting to start...</span>}
                                {batchState.logs.slice().reverse().map((log, i) => (
                                    <div key={i} className={`flex gap-2 mb-1 ${log.status === 'error' ? 'text-red-400' : 'text-slate-200'}`}>
                                        <span>[{log.status === 'error' ? 'FAIL' : 'OK'}]</span>
                                        <span className="text-gray-300">{log.email}</span>
                                        {log.status === 'error' && <span className="text-red-500">- {log.msg}</span>}
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t bg-gray-50 flex justify-end gap-3">
                            {batchState.completed ? (
                                <button 
                                    onClick={() => setBatchState(prev => ({...prev, active: false, completed: false}))}
                                    className="px-6 py-2 bg-gray-900 text-white rounded hover:bg-black font-medium"
                                >
                                    Close & Finish
                                </button>
                            ) : (
                                <button 
                                    onClick={() => {
                                        if(window.confirm("Batalkan sisa pengiriman?")) {
                                            setBatchState(prev => ({...prev, active: false}));
                                        }
                                    }}
                                    className="px-4 py-2 text-red-600 hover:bg-red-50 rounded font-medium text-sm"
                                >
                                    Cancel Broadcast
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            
            {/* Preview Modal */}
            {showPreview && (
                <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
                        <div className="flex items-center justify-between p-4 border-b">
                            <h3 className="text-lg font-bold">{previewTitle}</h3>
                            <button 
                                onClick={() => setShowPreview(false)}
                                className="p-2 hover:bg-gray-100 rounded"
                            >
                                <LuX className="w-5 h-5"/>
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto p-4">
                            <iframe 
                                srcDoc={previewHtml}
                                className="w-full h-[600px] border rounded"
                                title="Email Preview"
                            />
                        </div>
                        <div className="p-4 border-t flex gap-3">
                            <button 
                                onClick={() => setShowPreview(false)}
                                className="flex-1 py-2 px-4 border rounded font-medium hover:bg-gray-50"
                            >
                                Close
                            </button>
                            <button 
                                onClick={() => { setShowPreview(false); handleSend(); }}
                                disabled={sending || (selectedEmails.size === 0 && !manualEmails.trim())}
                                className="flex-1 py-2 px-4 bg-gray-900 text-white rounded font-medium hover:bg-black disabled:opacity-40"
                            >
                                <LuSend className="inline mr-2"/> Send Now
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
